// Aufbereitung des Druckprotokolls (Verwaltung → Druckprotokoll, Kundenwunsch 05.11.2026, wörtlich „noch eine extra
// tabelle so wie verkäufe einzuführen, auf denen alle drucke zu sehen sind? wann wo von wem etc?“ und „verkäufe in der
// ansicht nur vereinfacht darstellen logischerweise“). Eingang: ticketsale?option=printLog&from=YYYY-MM-DD&to=YYYY-MM-DD
// (bconn-aws, ticketsale-get; höchstens 62 Tage) → { from, to, generated_at, rows } mit rows zeitlich aufsteigend:
//   source 'sale' | 'print', id ('S' + upload_id bzw. 'P' + print_log_id), kind (VERKAUF, STORNO oder print_type),
//   time / end_time (Ortszeit-Text), printer_id, printer_comment, device_id, user_id, username, description,
//   price, ticket_count (nur Verkauf), line_id, trip_nr, stop_name, gps_lat, gps_lon (Druck ab App 3.0.6),
//   roll_numbers (je 10 Ziffern: 6 Rolle, 4 Abschnitt), paper_feed_mm, printer_type, printer_serial, has_log,
//   print_message, app_version, printer_status, autoload_fail, unknown_paper_position.
// Jedes Feld darf fehlen (ältere Einträge, älteres Backend). Arten und Etiketten: printKinds.js.
import { KIND_GROUPS, KNOWN_KINDS, kindGroup, kindLabel, kindTone } from './printKinds';
import { toRanges, rangeText } from '../rolls/rollTracking';
import { foldText, naturalKey } from '../sales/saleFilters';
import { formatNumber, LEER } from '../../utils/format';

// Zeitraum der Schnittstelle (ticketsale-get printLog): höchstens 62 Tage
export const MAX_DAYS = 62;

const ROLL_NUMBER = /^\d{10}$/;

function present(value) {
  return value !== null && value !== undefined && value !== '';
}

function textOrNull(value) {
  return present(value) ? String(value) : null;
}

function numberOrNull(value) {
  if (!present(value)) {
    return null;
  }
  const n = Number(value);
  return isFinite(n) ? n : null;
}

// Rollennummern je Rolle in Reihenfolge des ersten Auftretens: [{ roll, sections: [120, 121], text }]
export function rollGroups(numbers) {
  const groups = [];
  const byRoll = {};
  (Array.isArray(numbers) ? numbers : []).forEach((value) => {
    const raw = String(value === null || value === undefined ? '' : value).trim();
    if (!ROLL_NUMBER.test(raw)) {
      return;
    }
    const roll = raw.slice(0, 6);
    if (!byRoll[roll]) {
      byRoll[roll] = { roll, sections: [] };
      groups.push(byRoll[roll]);
    }
    const section = Number(raw.slice(6));
    if (byRoll[roll].sections.indexOf(section) === -1) {
      byRoll[roll].sections.push(section);
    }
  });
  return groups.map((g) => {
    const sorted = g.sections.slice().sort((a, b) => a - b);
    const ranges = toRanges(sorted).map((r) => rangeText(r[0], r[1])).join(', ');
    return { roll: g.roll, sections: sorted, text: g.roll + ' · ' + ranges };
  });
}

// kompakt „034825 · 120–125“, mehrere Rollen „034825 · 748–750 / 034826 · 0–2“; ohne Rollennummer ''
export function rollText(numbers) {
  return rollGroups(numbers).map((g) => g.text).join(' / ');
}

// GPS nur mit gültigen Koordinaten (0/0 = kein Signal)
export function hasPosition(lat, lon) {
  const a = numberOrNull(lat);
  const b = numberOrNull(lon);
  return a !== null && b !== null && Math.abs(a) <= 90 && Math.abs(b) <= 180 && !(a === 0 && b === 0);
}

// Link auf OpenStreetMap mit Markierung, Zoomstufe 17
export function osmUrl(lat, lon) {
  if (!hasPosition(lat, lon)) {
    return null;
  }
  const a = Number(Number(lat).toFixed(6));
  const b = Number(Number(lon).toFixed(6));
  return 'https://www.openstreetmap.org/?mlat=' + a + '&mlon=' + b + '#map=17/' + a + '/' + b;
}

// „48,12345, 11,56789“
export function positionText(lat, lon) {
  return hasPosition(lat, lon) ? formatNumber(lat, 5) + ', ' + formatNumber(lon, 5) : '';
}

// „Linie 582 · Fahrt 58201“ (0 gilt als ohne)
export function lineTripText(row) {
  const parts = [];
  if (present(row.line_id) && String(row.line_id) !== '0') {
    parts.push('Linie ' + row.line_id);
  }
  if (present(row.trip_nr) && String(row.trip_nr) !== '0') {
    parts.push('Fahrt ' + row.trip_nr);
  }
  return parts.join(' · ');
}

export function isGebe(row) {
  return String(row.printer_type || '').toUpperCase() === 'GEBE';
}

// Meldungen einer Zeile, wichtigste zuerst: [{ level: 'error' | 'warn', text }]. Druckmeldung ungleich „success“ (Fehler
// beim Druck), gemeldeter Fehlschlag des Papiereinzugs, unbekannte Papierposition und „keine Rollennummer“ bei Verkauf
// oder Druck ohne Verkauf auf einem GeBE-Drucker (Positionsmeldungen und Zustände lesen oft keine Stelle: kein Hinweis)
export function messagesOf(row) {
  const list = [];
  const message = textOrNull(row.print_message);
  if (message && message.trim() && message.trim().toLowerCase() !== 'success') {
    list.push({ level: 'error', text: message.trim() });
  }
  if (row.autoload_fail) {
    list.push({ level: 'warn', text: 'Papiereinzug fehlgeschlagen' });
  }
  if (row.unknown_paper_position) {
    list.push({ level: 'warn', text: 'Papierposition unbekannt' });
  }
  const group = kindGroup(row.kind);
  if ((group === 'sale' || group === 'print') && isGebe(row) && !rollGroups(row.roll_numbers).length) {
    list.push({ level: 'warn', text: 'keine Rollennummer' });
  }
  return list;
}

// Zeile der Schnittstelle → Anzeigezeile (eingefroren, Vue beobachtet sie nicht). seq = Stelle in der zeitlichen Folge
// (Sortierwert der Zeit, bei gleicher Sekunde stabil)
export function normalizeRow(raw, seq) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const kind = textOrNull(r.kind) || (r.source === 'sale' ? 'VERKAUF' : '');
  const row = {
    source: r.source === 'sale' ? 'sale' : 'print',
    id: textOrNull(r.id) || 'X' + seq,
    seq,
    kind,
    time: textOrNull(r.time),
    end_time: textOrNull(r.end_time),
    printer_id: present(r.printer_id) ? r.printer_id : null,
    printer_comment: textOrNull(r.printer_comment),
    device_id: textOrNull(r.device_id),
    user_id: present(r.user_id) ? r.user_id : null,
    username: textOrNull(r.username),
    description: textOrNull(r.description),
    price: numberOrNull(r.price),
    ticket_count: numberOrNull(r.ticket_count),
    line_id: present(r.line_id) ? r.line_id : null,
    trip_nr: textOrNull(r.trip_nr),
    stop_name: textOrNull(r.stop_name),
    gps_lat: numberOrNull(r.gps_lat),
    gps_lon: numberOrNull(r.gps_lon),
    roll_numbers: Array.isArray(r.roll_numbers) ? r.roll_numbers.map(String) : null,
    paper_feed_mm: numberOrNull(r.paper_feed_mm),
    printer_type: textOrNull(r.printer_type),
    printer_serial: textOrNull(r.printer_serial),
    has_log: !!r.has_log,
    print_message: textOrNull(r.print_message),
    app_version: textOrNull(r.app_version),
    printer_status: textOrNull(r.printer_status),
    autoload_fail: !!r.autoload_fail,
    unknown_paper_position: !!r.unknown_paper_position
  };
  row.group = kindGroup(kind);
  row.tone = kindTone(kind);
  row.kind_label = kindLabel(kind);
  row.roll_groups = rollGroups(row.roll_numbers);
  row.roll_text = row.roll_groups.map((g) => g.text).join(' / ');
  row.paper_text = row.paper_feed_mm !== null && row.paper_feed_mm > 0 ? formatNumber(row.paper_feed_mm, 1) + ' mm' : '';
  row.line_trip = lineTripText(row);
  row.osm_url = osmUrl(row.gps_lat, row.gps_lon);
  row.messages = messagesOf(row);
  row.message_text = row.messages.map((m) => m.text).join(' · ');
  row.message_level = row.messages.length ? row.messages[0].level : null;
  // Suchtext ohne Benutzernamen (die vollen Namen lädt die Seite nach und prüft sie beim Suchen dazu)
  row.search_text = foldText([
    row.kind_label, row.description, row.printer_id, row.printer_comment, row.username, row.stop_name, row.line_trip,
    row.roll_numbers ? row.roll_numbers.join(' ') : '', row.roll_text, row.message_text, row.printer_serial, row.device_id,
    row.price !== null ? formatNumber(row.price, 2) : ''
  ].filter(present).join(' '));
  return Object.freeze(row);
}

export function normalizeRows(rows) {
  return Object.freeze((Array.isArray(rows) ? rows : []).map((raw, i) => normalizeRow(raw, i)));
}

// ---------------------------------------------------------------- Filter der Werkzeugleiste
// Art: null (alle), 'g:sale' | 'g:print' | 'g:state' (Gruppe) oder 'k:<kind>' (eine Art)
export function matchesKind(row, value) {
  if (!value) {
    return true;
  }
  if (value.indexOf('g:') === 0) {
    return row.group === value.slice(2);
  }
  return value.indexOf('k:') === 0 ? row.kind === value.slice(2) : true;
}

// Drucker: null (alle), 'none' (ohne Drucker-Nr.) oder die Nummer als Text
export function matchesPrinter(row, value) {
  if (!value) {
    return true;
  }
  if (value === 'none') {
    return row.printer_id === null;
  }
  return row.printer_id !== null && String(row.printer_id) === value;
}

// query: schon mit foldText aufbereitet; userLabel(row) liefert den angezeigten Benutzernamen
export function matchesSearch(row, query, userLabel) {
  if (!query) {
    return true;
  }
  if (row.search_text.indexOf(query) !== -1) {
    return true;
  }
  return !!userLabel && foldText(userLabel(row) || '').indexOf(query) !== -1;
}

function countText(text, n) {
  return n ? text + ' (' + formatNumber(n) + ')' : text;
}

// Einträge des Art-Filters: alle, die drei Gruppen, darunter jede vorkommende Art mit Anzahl. selected: gewählter Wert,
// der auch ohne Treffer (anderer Zeitraum) in der Liste bleibt
export function kindItems(rows, selected) {
  const counts = {};
  const groups = {};
  (rows || []).forEach((row) => {
    counts[row.kind] = (counts[row.kind] || 0) + 1;
    groups[row.group] = (groups[row.group] || 0) + 1;
  });
  if (selected && selected.indexOf('k:') === 0 && !counts[selected.slice(2)]) {
    counts[selected.slice(2)] = 0;
  }
  const items = [{ value: null, text: 'Alle Arten', buttonText: 'Art', ariaText: 'alle' }];
  // Knopftext ohne Anzahl
  KIND_GROUPS.forEach((g) => items.push({ value: 'g:' + g.key, text: countText(g.text, groups[g.key]), buttonText: g.text }));
  // bekannte Arten in fester Folge, unbekannte danach nach Etikett
  const rank = (kind) => (KNOWN_KINDS.indexOf(kind) === -1 ? KNOWN_KINDS.length : KNOWN_KINDS.indexOf(kind));
  const kinds = Object.keys(counts).sort((a, b) => rank(a) - rank(b) || kindLabel(a).localeCompare(kindLabel(b), 'de'));
  if (kinds.length) {
    items.push({ divider: true }, { header: 'Je Art' });
    kinds.forEach((kind) => items.push({
      value: 'k:' + kind, text: countText(kindLabel(kind), counts[kind]), buttonText: kindLabel(kind)
    }));
  }
  return items;
}

// Einträge des Drucker-Filters: alle, dann je Drucker „10003 · Bus 12“ in natürlicher Folge; selected wie bei kindItems
export function printerItems(rows, selected) {
  const seen = {};
  let none = 0;
  (rows || []).forEach((row) => {
    if (row.printer_id === null) {
      none += 1;
      return;
    }
    const key = String(row.printer_id);
    if (!seen[key]) {
      seen[key] = { id: key, comment: null, count: 0 };
    }
    seen[key].count += 1;
    seen[key].comment = seen[key].comment || row.printer_comment;
  });
  if (selected && selected !== 'none' && !seen[selected]) {
    seen[selected] = { id: selected, comment: null, count: 0 };
  }
  const items = [{ value: null, text: 'Alle Drucker', buttonText: 'Drucker', ariaText: 'alle' }];
  Object.keys(seen).map((key) => seen[key])
    .sort((a, b) => naturalKey(a.id).localeCompare(naturalKey(b.id)))
    .forEach((p) => items.push({
      value: p.id,
      text: countText(p.comment ? p.id + ' · ' + p.comment : p.id, p.count),
      buttonText: 'Drucker ' + p.id,
      ariaText: p.comment ? p.id + ' (' + p.comment + ')' : p.id
    }));
  if (none || selected === 'none') {
    items.push({ value: 'none', text: countText('Ohne Drucker-Nr.', none), buttonText: 'Ohne Drucker-Nr.' });
  }
  return items;
}

// Fußleiste: Anzahl je Gruppe und mit Meldung
export function summarize(rows) {
  const s = { total: 0, sale: 0, print: 0, state: 0, other: 0, messages: 0 };
  (rows || []).forEach((row) => {
    s.total += 1;
    s[row.group] = (s[row.group] || 0) + 1;
    if (row.messages.length) {
      s.messages += 1;
    }
  });
  return s;
}

// ---------------------------------------------------------------- Zeitraum
function day(date) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const pad = (n) => (n < 10 ? '0' : '') + n;
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
}

function addDays(date, n) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + n);
}

function parseDay(text) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(text || ''));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

export function today(now = new Date()) {
  return day(now);
}

// Schnellwahl im Zeitraum-Feld: Heute, Gestern, 7 Tage, 30 Tage (jeweils bis heute)
export function periodPresets(now = new Date()) {
  const t = day(now);
  return [
    { text: 'Heute', from: t, to: t },
    { text: 'Gestern', from: day(addDays(now, -1)), to: day(addDays(now, -1)) },
    { text: '7 Tage', from: day(addDays(now, -6)), to: t },
    { text: '30 Tage', from: day(addDays(now, -29)), to: t }
  ];
}

// Zeitraum auf höchstens MAX_DAYS Tage kürzen (bis-Tag bleibt): { from, to, clamped }
export function clampPeriod(from, to) {
  const a = parseDay(from);
  const b = parseDay(to);
  if (!a || !b) {
    return { from, to, clamped: false };
  }
  const start = a <= b ? a : b;
  const end = a <= b ? b : a;
  const days = Math.round((end - start) / 86400000) + 1;
  if (days <= MAX_DAYS) {
    return { from: day(start), to: day(end), clamped: false };
  }
  return { from: day(addDays(end, -(MAX_DAYS - 1))), to: day(end), clamped: true };
}

// Anzeige eines fehlenden Werts
export const EMPTY = LEER;
